import { useEffect, useRef, useState } from 'react';

export default function TestimonialsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      name: '田中 美咲ちゃん（8歳）のお母様',
      comment: 'うちの子が初めて1時間集中したんです！いつもは10分も座っていられないのに、パソコンの前では目がキラキラしていました。先生の教え方が本当に優しくて、娘も「また行きたい！」と毎週楽しみにしています。',
      rating: 5,
      avatar: '👩',
      delay: '0s'
    },
    {
      name: '佐藤 健太くん（10歳）のお父様',
      comment: '私自身がITに疎くて、子供に何を教えたらいいか分からなかったんです。でもここは非エンジニアの親の気持ちを分かってくれて、子供だけでなく親にも丁寧に説明してくれます。息子も自信がついてきました。',
      rating: 5,
      avatar: '👨',
      delay: '0.2s'
    },
    {
      name: '鈴木 さくらちゃん（7歳）のお母様',
      comment: '他のスクールは難しすぎて娘が泣いてしまったのですが、ここは本当に優しく教えてくれます。「わからない」と言っても怒られないから、娘も安心して質問できるみたいです。月謝も良心的で助かります。',
      rating: 5,
      avatar: '👩',
      delay: '0.4s'
    },
    {
      name: '山田 大輝くん（9歳）のお父様',
      comment: 'PCを買うのを躊躇していたのですが、レンタルできるので気軽に始められました。息子は今では自分でゲームを作りたいと言い出して、将来の夢ができたみたいです。本当に感謝しています。',
      rating: 5,
      avatar: '👨',
      delay: '0.6s'
    }
  ];

  const achievements = [
    { number: '200+', label: '受講生徒数', icon: '👦' },
    { number: '98%', label: '保護者満足度', icon: '❤️' },
    { number: '1時間', label: '平均集中時間', icon: '⏰' },
    { number: '100%', label: '継続率', icon: '📈' }
  ];

  return (
    <section ref={sectionRef} id="testimonials" className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-yellow-50 to-pink-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-purple-200 rounded-full opacity-10 blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 left-10 w-64 h-64 bg-pink-200 rounded-full opacity-10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-10 sm:mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-block bg-pink-100 px-4 sm:px-6 py-2 rounded-full mb-4 sm:mb-6">
            <span className="text-pink-600 font-bold text-xs sm:text-sm">保護者様の声</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-800 mb-4 sm:mb-6 px-2">
            <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              たくさんの笑顔が生まれています
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600">実際に通われている保護者様からの声</p>
        </div>

        {/* Achievements */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-20 transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          {achievements.map((achievement, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg text-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300"
            >
              <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">{achievement.icon}</div>
              <div className="text-2xl sm:text-3xl md:text-4xl font-black bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent mb-1 sm:mb-2">
                {achievement.number}
              </div>
              <div className="text-gray-600 font-medium text-xs sm:text-sm">{achievement.label}</div>
            </div>
          ))}
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
              style={{ transitionDelay: testimonial.delay }}
            >
              {/* Avatar and Name */}
              <div className="flex items-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                <div className="w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-br from-pink-400 to-purple-400 rounded-full flex items-center justify-center text-2xl sm:text-3xl shadow-lg flex-shrink-0">
                  {testimonial.avatar}
                </div>
                <div>
                  <h4 className="font-bold text-base sm:text-lg text-gray-800">{testimonial.name}</h4>
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-yellow-400 text-base sm:text-xl">⭐</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Comment */}
              <div className="relative">
                <div className="absolute -top-2 -left-2 text-4xl sm:text-6xl text-pink-200 opacity-50">"</div>
                <p className="text-gray-700 text-sm sm:text-lg leading-relaxed relative z-10 pl-4 sm:pl-6">
                  {testimonial.comment}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Special Message */}
        <div className={`mt-12 sm:mt-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl sm:rounded-3xl p-6 sm:p-10 md:p-16 shadow-2xl text-center transform transition-all duration-1000 delay-700 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
          <div className="max-w-3xl mx-auto">
            <div className="text-4xl sm:text-5xl mb-4 sm:mb-6">✨</div>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-800 mb-4 sm:mb-6 px-2">
              モニター受講生の<span className="text-pink-500">100%</span>が<br />
              「また来たい！」と回答
            </h3>
            <p className="text-base sm:text-xl text-gray-700 leading-relaxed mb-6 sm:mb-8 px-2">
              お子様の「できた！」という喜びの瞬間を、<br className="hidden sm:block" />
              私たちと一緒に見守りませんか？
            </p>
            <a 
              href="#trial"
              className="inline-block bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 sm:px-10 py-4 sm:py-5 rounded-full font-black text-base sm:text-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              無料体験で実感してください
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
